import * as Sentry from "@sentry/react";
import type { PostHog } from "posthog-js";
import { z } from "zod";
import {
	type DefectAttachmentMeta,
	type DefectPosthogLinks,
	type DefectReportPayload,
	type DefectUserSummary,
	buildDefectReportPayload,
	buildSentryUrls,
	defectReportPayloadSchema,
} from "./defect-context";
import {
	type DiagnosticSnapshot,
	getDiagnosticSnapshot,
} from "./diagnostic-ring-buffer";
import { submitFiberyDefect } from "./submit-fibery-defect";

const SENTRY_FLUSH_TIMEOUT_MS = 2_500;

export const defectSubmissionResultSchema = z.object({
	payload: defectReportPayloadSchema,
	posthogSubmitted: z.boolean(),
	sentrySubmitted: z.boolean(),
	fiberyQueued: z.boolean(),
});

export type DefectSubmissionResult = z.infer<
	typeof defectSubmissionResultSchema
>;

export interface DefectOpenSnapshot {
	occurredAt: string;
	diagnostics: DiagnosticSnapshot;
	posthogLinks: DefectPosthogLinks;
}

export interface SubmitDefectReportOptions {
	note?: string;
	files?: File[];
	user?: DefectUserSummary | null;
	awaitFibery?: boolean;
	openSnapshot?: DefectOpenSnapshot | null;
	posthog?: Pick<
		PostHog,
		| "capture"
		| "get_session_id"
		| "get_distinct_id"
		| "get_session_replay_url"
		| "sessionRecordingStarted"
		| "startSessionRecording"
	> | null;
}

interface BinaryAttachment {
	filename: string;
	data: string | Uint8Array;
	contentType: string;
}

function createReportId(): string {
	if (
		typeof crypto !== "undefined" &&
		typeof crypto.randomUUID === "function"
	) {
		return crypto.randomUUID();
	}
	return `defect-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function collectPosthogLinks(
	posthog: SubmitDefectReportOptions["posthog"],
): DefectPosthogLinks {
	if (!posthog) {
		return {
			sessionId: null,
			distinctId: null,
			replayUrl: null,
			recordingActive: false,
		};
	}

	try {
		const wasStarted = Boolean(posthog.sessionRecordingStarted?.());
		if (!wasStarted) {
			posthog.startSessionRecording?.(true);
		}
		const recordingActive = Boolean(
			wasStarted || posthog.sessionRecordingStarted?.(),
		);
		const sessionId = posthog.get_session_id?.() || null;
		const distinctId = posthog.get_distinct_id?.() || null;
		const replayUrl =
			posthog.get_session_replay_url?.({
				withTimestamp: true,
				timestampLookBack: 60,
			}) || null;

		return {
			sessionId,
			distinctId,
			replayUrl,
			recordingActive,
		};
	} catch {
		return {
			sessionId: null,
			distinctId: null,
			replayUrl: null,
			recordingActive: false,
		};
	}
}

async function flushSentryReplayWithTimeout(): Promise<string | null> {
	try {
		const replay = Sentry.getReplay();
		if (!replay) {
			return null;
		}
		await Promise.race([
			replay.flush(),
			new Promise<void>((resolve) => {
				setTimeout(resolve, SENTRY_FLUSH_TIMEOUT_MS);
			}),
		]);
		return replay.getReplayId() ?? null;
	} catch {
		return null;
	}
}

function readFileBytes(file: File): Promise<Uint8Array> {
	if (typeof file.arrayBuffer === "function") {
		return file.arrayBuffer().then((buf) => new Uint8Array(buf));
	}
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			if (reader.result instanceof ArrayBuffer) {
				resolve(new Uint8Array(reader.result));
			} else {
				reject(new Error("Unexpected FileReader result"));
			}
		};
		reader.onerror = () =>
			reject(reader.error ?? new Error("FileReader error"));
		reader.readAsArrayBuffer(file);
	});
}

async function readFilesForSentry(files: File[]): Promise<BinaryAttachment[]> {
	const results: BinaryAttachment[] = [];
	for (const file of files) {
		try {
			const data = await readFileBytes(file);
			results.push({
				filename: file.name,
				data,
				contentType: file.type || "application/octet-stream",
			});
		} catch {
			// Skip unreadable file
		}
	}
	return results;
}

function submitToSentry(
	payload: DefectReportPayload,
	fileAttachments: BinaryAttachment[],
): {
	submitted: boolean;
	eventId: string | null;
} {
	try {
		const message =
			payload.note ||
			`[1-Click Defect Report] User reported an issue on ${payload.pathname}`;

		const eventId = Sentry.withScope((scope) => {
			scope.setContext("defect_report", {
				reportId: payload.reportId,
				occurredAt: payload.occurredAt,
				environment: payload.environment,
				client: payload.client,
				appVersion: payload.appVersion,
				boardId: payload.boardId,
				cardId: payload.cardId,
				posthogSessionId: payload.posthog.sessionId,
				posthogReplayUrl: payload.posthog.replayUrl,
				sentryReplayId: payload.sentry.replayId,
				consoleLogCount: payload.diagnostics.consoleEntries.length,
				errorCount: payload.diagnostics.errorEntries.length,
				attachmentCount: payload.attachments.length,
			});

			return Sentry.captureFeedback(
				{
					message,
					name: payload.user?.displayName,
					email: payload.user?.email,
					url: payload.url,
					source: "defect_reporter",
					tags: {
						report_id: payload.reportId,
						environment: payload.environment,
						client: payload.client,
						app_version: payload.appVersion,
						board_id: payload.boardId ?? "none",
						card_id: payload.cardId ?? "none",
						posthog_session_id: payload.posthog.sessionId ?? "none",
					},
				},
				{
					includeReplay: true,
					attachments: [
						{
							filename: "defect-diagnostics.json",
							data: JSON.stringify(payload, null, 2),
							contentType: "application/json",
						},
						...fileAttachments,
					],
				},
			);
		});

		return {
			submitted: Boolean(eventId),
			eventId: eventId || null,
		};
	} catch {
		return {
			submitted: false,
			eventId: null,
		};
	}
}

function submitToPosthog(
	posthog: SubmitDefectReportOptions["posthog"],
	payload: DefectReportPayload,
): boolean {
	if (!posthog) {
		return false;
	}

	try {
		posthog.capture(
			"defect_reported",
			{
				report_id: payload.reportId,
				occurred_at: payload.occurredAt,
				environment: payload.environment,
				client: payload.client,
				app_version: payload.appVersion,
				board_id: payload.boardId,
				card_id: payload.cardId,
				note: payload.note,
				url: payload.url,
				pathname: payload.pathname,
				session_replay_url: payload.posthog.replayUrl,
				posthog_session_id: payload.posthog.sessionId,
				sentry_replay_id: payload.sentry.replayId,
				sentry_replay_url: payload.sentry.replayUrl,
				sentry_feedback_event_id: payload.sentry.feedbackEventId,
				sentry_feedback_url: payload.sentry.feedbackUrl,
				attachment_count: payload.attachments.length,
				attachments: payload.attachments,
				console_log_count: payload.diagnostics.consoleEntries.length,
				error_count: payload.diagnostics.errorEntries.length,
				recent_console_logs: payload.diagnostics.consoleEntries,
				recent_errors: payload.diagnostics.errorEntries,
				defect_payload: payload,
			},
			{ send_instantly: true },
		);
		return true;
	} catch {
		return false;
	}
}

export function captureDefectOpenSnapshot(
	posthog?: SubmitDefectReportOptions["posthog"],
): DefectOpenSnapshot {
	return {
		occurredAt: new Date().toISOString(),
		diagnostics: getDiagnosticSnapshot(),
		posthogLinks: collectPosthogLinks(posthog),
	};
}

export async function submitDefectReport(
	options: SubmitDefectReportOptions = {},
): Promise<DefectSubmissionResult> {
	const reportId = createReportId();
	const files = options.files ?? [];
	const attachmentsMeta: DefectAttachmentMeta[] = files.map((file) => ({
		name: file.name,
		contentType: file.type || "application/octet-stream",
		sizeBytes: file.size,
	}));

	const snapshot =
		options.openSnapshot ?? captureDefectOpenSnapshot(options.posthog);
	const [sentryReplayId, sentryFileAttachments] = await Promise.all([
		flushSentryReplayWithTimeout(),
		readFilesForSentry(files),
	]);

	const initialPayload = buildDefectReportPayload({
		reportId,
		occurredAt: snapshot.occurredAt,
		note: options.note ?? "",
		user: options.user ?? null,
		attachments: attachmentsMeta,
		posthog: snapshot.posthogLinks,
		sentry: {
			replayId: sentryReplayId,
			feedbackEventId: null,
		},
		diagnostics: snapshot.diagnostics,
	});

	const sentryResult = submitToSentry(initialPayload, sentryFileAttachments);
	const sentryUrls = buildSentryUrls({
		replayId: sentryReplayId,
		feedbackEventId: sentryResult.eventId,
		reportId,
	});

	const finalPayload = defectReportPayloadSchema.parse({
		...initialPayload,
		sentry: {
			replayId: sentryReplayId,
			feedbackEventId: sentryResult.eventId,
			replayUrl: sentryUrls.replayUrl,
			feedbackUrl: sentryUrls.feedbackUrl,
		},
	});

	const posthogSubmitted = submitToPosthog(options.posthog, finalPayload);

	const fiberyTask = submitFiberyDefect(finalPayload, files);
	if (options.awaitFibery) {
		await fiberyTask;
	} else {
		void fiberyTask;
	}

	if (typeof window !== "undefined") {
		window.dispatchEvent(
			new CustomEvent("illumi:defect-reported", { detail: finalPayload }),
		);
	}

	return defectSubmissionResultSchema.parse({
		payload: finalPayload,
		posthogSubmitted,
		sentrySubmitted: sentryResult.submitted,
		fiberyQueued: true,
	});
}
